import picomatch from 'picomatch';
import z from 'zod/v4';

import { integrationSchemaRegistry } from './integration';

export enum PatternFilterType {
  Include = 'include',
  Exclude = 'exclude',
}

export const inclusionPatternFilterSchema = z
  .object({
    type: z.literal(PatternFilterType.Include).register(integrationSchemaRegistry, {
      i18n: 'patternFilter.filterType',
    }),
    patterns: z.array(z.string().min(1)).min(1),
  })
  .register(integrationSchemaRegistry, { i18n: 'patternFilter.filter.include' });

export const exclusionPatternFilterSchema = z
  .object({
    type: z.literal(PatternFilterType.Exclude).register(integrationSchemaRegistry, {
      i18n: 'patternFilter.filterType',
    }),
    patterns: z.array(z.string().min(1)).min(1),
  })
  .register(integrationSchemaRegistry, { i18n: 'patternFilter.filter.exclude' });

export const patternFilterSchema = z.discriminatedUnion('type', [
  inclusionPatternFilterSchema,
  exclusionPatternFilterSchema,
]);

export type InclusionPatternFilter = z.infer<typeof inclusionPatternFilterSchema>;
export type ExclusionPatternFilter = z.infer<typeof exclusionPatternFilterSchema>;
export type PatternFilter = z.infer<typeof patternFilterSchema>;

export function createPatternFilterPredicate(
  patternFilter?: PatternFilter,
): (key: string) => boolean {
  if (!patternFilter) return () => true;
  const matchFunction = picomatch(patternFilter.patterns.map(foldApostrophes), { nocase: true });
  return (key: string) => {
    const matches = matchFunction(foldApostrophes(key));
    return patternFilter.type === PatternFilterType.Include ? matches : !matches;
  };
}

/**
 * Third-party systems and the humans typing patterns into the config form mix
 * typographic apostrophes with the ASCII one, so `FY’26` and `FY'26` have to match
 * the same campaign. Folding both sides keeps them comparable. Only apostrophes are folded: picomatch treats
 * `'` as a literal character but `"` as shell quoting, so touching double quotes
 * would change the meaning of saved patterns, therefore ignoring them.
 */
const APOSTROPHE_VARIANTS = /[\u2018\u2019\u02BC]/g;

function foldApostrophes(value: string): string {
  return value.replace(APOSTROPHE_VARIANTS, "'");
}
